'use client';

import { Suspense, useCallback, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { getCommunityFeed, getCommunityPost } from '@/lib/api-client';
import SiteHeader from '@/app/components/SiteHeader';
import MediaTile from '@/app/components/community/MediaTile';
import PostModal from '@/app/components/PostModal';
import Spinner from '@/app/components/Spinner';
import { Stagger, StaggerItem } from '@/app/components/motion';
import type { FeedPost } from '@/app/components/community/types';

/**
 * Public, logged-out community browse. Reuses the (already public, optionalAuth) feed +
 * the MediaTile/PostModal so guests can explore posts; any interaction (like/comment/
 * follow/post) redirects to login. Logged-in users are sent to the full dashboard version.
 */
function PublicCommunityInner() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [activePost, setActivePost] = useState<FeedPost | null>(null);

  // Logged-in visitors get the full experience.
  useEffect(() => {
    if (!authLoading && user) router.replace('/dashboard/community');
  }, [authLoading, user, router]);

  const load = useCallback(async (next?: string) => {
    if (next) setLoadingMore(true);
    else setLoading(true);
    try {
      const res = await getCommunityFeed({ sort: 'new', cursor: next, limit: 24 });
      const data = res?.data ?? res;
      const newPosts: FeedPost[] = data?.posts || [];
      setPosts((prev) => (next ? [...prev, ...newPosts] : newPosts));
      setCursor(data?.nextCursor ?? null);
      setHasMore(!!data?.nextCursor);
      setError(false);
    } catch {
      if (!next) setError(true);
    } finally {
      if (next) setLoadingMore(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Open a shared post via ?post=<id> (single post + comments are public).
  useEffect(() => {
    const id = searchParams.get('post');
    if (!id) return;
    (async () => {
      try {
        const res = await getCommunityPost(id);
        const p = (res?.data ?? res)?.post ?? (res?.data ?? res);
        if (p?.id) setActivePost(p as FeedPost);
      } catch {
        /* ignore */
      }
    })();
  }, [searchParams]);

  const requireLogin = () => router.push('/?auth=login&redirect=/community');

  const handleShare = (post: FeedPost) => {
    try {
      navigator.clipboard?.writeText(`${window.location.origin}/community?post=${post.id}`);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-16">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-semibold text-white">Community</h1>
          <p className="mt-1 text-sm text-white/50">
            Explore what people are creating on Ultragoon.{' '}
            <button onClick={requireLogin} className="text-purple-300 hover:text-purple-200 cursor-pointer">
              Sign in
            </button>{' '}
            to like, comment, and post your own.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Spinner size={28} className="text-white/40" />
          </div>
        ) : error ? (
          <div className="py-24 text-center text-sm text-white/40">Couldn’t load the community feed.</div>
        ) : posts.length === 0 ? (
          <div className="py-24 text-center text-sm text-white/40">No posts yet — check back soon.</div>
        ) : (
          <>
            <Stagger className="columns-2 gap-4 sm:columns-3 sm:gap-5 lg:columns-4">
              {posts.map((post) => (
                <StaggerItem key={post.id} className="mb-4 break-inside-avoid sm:mb-5">
                  <MediaTile
                    post={post}
                    currentUserId={undefined}
                    isFollowing={false}
                    followPending={false}
                    onLike={requireLogin}
                    onOpenPost={() => setActivePost(post)}
                    onNavigate={requireLogin}
                    onFollow={requireLogin}
                    onShare={handleShare}
                  />
                </StaggerItem>
              ))}
            </Stagger>
            {hasMore && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => cursor && load(cursor)}
                  disabled={loadingMore}
                  className="rounded-lg bg-white/[0.06] px-5 py-2.5 text-sm font-medium text-white/80 transition-colors hover:bg-white/[0.1] disabled:opacity-50 cursor-pointer"
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {activePost && (
        <PostModal
          post={activePost}
          currentUserId={undefined}
          canComment={false}
          onClose={() => setActivePost(null)}
          onLike={requireLogin}
          onDelete={requireLogin}
          onReport={requireLogin}
          onCommentAdded={requireLogin}
        />
      )}
    </div>
  );
}

export default function PublicCommunityPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#0a0a0a]" />}>
      <PublicCommunityInner />
    </Suspense>
  );
}
